import { BodyText } from '@/components/BodyText';
import { Box, ButtonGroup, TableBody } from '@mui/material';
import CompareIcon from '@mui/icons-material/Compare';
import { MatchIcon } from '../AssetsPage/MatchIcon';
import { ExpandableItem } from './ExpandableItem';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Add } from '@mui/icons-material';
import { HelpIcon } from './HelpIcon';
import { HelpLink } from './HelpLink';
import { StyledHelpViewButton } from './StyledHelpButton';
import {
  StyledTable,
  StyledTableHeadRow,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
  TableHead,
} from './HelpTable';

export enum CompareSubView {
  FindingMatches = 'findingMatches',
  ComparingParts = 'comparingParts',
}

export const MatchAndCompareHelpView = ({
  isOpen,
  onToggle,
  viewParameter,
  setCompareView,
}: {
  isOpen: boolean;
  onToggle: () => void;
  viewParameter: CompareSubView | null;
  setCompareView: (view: string) => () => void;
}) => {
  const currentView = viewParameter ?? CompareSubView.FindingMatches;

  return (
    <ExpandableItem
      isOpen={isOpen}
      onToggle={onToggle}
      title={<>2. Finding matches and comparing parts and assemblies</>}
      description={
        <>
          <Box sx={{ marginBottom: '12px' }}>
            <ButtonGroup size='small' variant='outlined'>
              <StyledHelpViewButton
                onClick={setCompareView(CompareSubView.FindingMatches)}
                variant={currentView === CompareSubView.FindingMatches ? 'contained' : 'outlined'}
              >
                Finding matches
              </StyledHelpViewButton>
              <StyledHelpViewButton
                onClick={setCompareView(CompareSubView.ComparingParts)}
                variant={currentView === CompareSubView.ComparingParts ? 'contained' : 'outlined'}
              >
                Comparing parts and assemblies
              </StyledHelpViewButton>
            </ButtonGroup>
          </Box>
          {viewParameter === CompareSubView.FindingMatches && <FindingMatchesView />}
          {viewParameter === CompareSubView.ComparingParts && <ComparingPartsView />}
          {viewParameter === null && <DefaultFindingMatchesView />}
        </>
      }
    />
  );
};

const FindingMatchesView = () => {
  const { isMetadataSearchEnabled, isSuitableSubstitutesEnabled } = useFeatureFlags();

  return (
    <>
      <BodyText component='p' sx={{ marginBottom: '12px' }}>
        Click{' '}
        <HelpIcon>
          <MatchIcon sx={{ fontSize: '16px' }} />
        </HelpIcon>{' '}
        to find matches for a part or assembly or click{' '}
        <HelpIcon>
          <Add sx={{ fontSize: '16px' }} />
        </HelpIcon>{' '}
        from the <HelpLink to='assets'>Assets</HelpLink> page to select an asset or upload files. Adjust match threshold
        and filters to refine results.
      </BodyText>

      <StyledTable size='small' sx={{ marginTop: '12px' }}>
        <TableHead>
          <StyledTableHeadRow>
            <StyledTableHeadCell sx={{ whiteSpace: 'nowrap' }}>Match Type</StyledTableHeadCell>
            <StyledTableHeadCell>Description</StyledTableHeadCell>
          </StyledTableHeadRow>
        </TableHead>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>
              <BodyText color='text.primary'>Geometric</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds parts with similar geometry. Use this to find duplicate candidates, or simplification
                opportunities.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow isEven>
            <StyledTableCell>
              <BodyText color='text.primary'>Part</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds assemblies that contain parts similar to your selected part. Use this when you want to understand
                where similar parts appear across assemblies.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>
              <BodyText color='text.primary'>Visual</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds parts that look similar. Use this when appearance is more important than part geometry.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          {isMetadataSearchEnabled && (
            <StyledTableRow isEven>
              <StyledTableCell>
                <BodyText color='text.primary'>Metadata</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  Finds parts with similar metadata values. Use this when part numbers, material, category, or other
                  fields are your primary matching signal.
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
          )}
          {isSuitableSubstitutesEnabled && (
            <StyledTableRow isEven={!isMetadataSearchEnabled}>
              <StyledTableCell>
                <BodyText color='text.primary'>Substitutes</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  Suggests part substitutes. Use this when you&apos;re looking for interchangeable options rather than
                  strict duplicates.
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </StyledTable>
    </>
  );
};

const ComparingPartsView = () => {
  return (
    <>
      <BodyText component='p'>
        Click{' '}
        <HelpIcon>
          <CompareIcon sx={{ fontSize: '16px' }} />
        </HelpIcon>{' '}
        to compare parts and assemblies, metadata, and relationships to make informed decisions about duplicates,
        substitutes, or simplification opportunities.
      </BodyText>
      <StyledTable size='small' sx={{ marginTop: '12px' }}>
        <TableHead>
          <StyledTableHeadRow>
            <StyledTableHeadCell sx={{ whiteSpace: 'nowrap' }}>Comparison Type</StyledTableHeadCell>
            <StyledTableHeadCell>Description</StyledTableHeadCell>
          </StyledTableHeadRow>
        </TableHead>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>
              <BodyText color='text.primary'>Side by Side</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                View two parts or assemblies side by side to compare their geometry, structure, and visual differences.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow isEven>
            <StyledTableCell>
              <BodyText color='text.primary'>Overlay</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Overlay two parts or assemblies on top of each other to see exact differences and similarities in a
                single view.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
        </TableBody>
      </StyledTable>
    </>
  );
};

const DefaultFindingMatchesView = () => {
  const { isMetadataSearchEnabled, isSuitableSubstitutesEnabled } = useFeatureFlags();

  return (
    <>
      <BodyText component='p' sx={{ marginBottom: '12px' }}>
        Click{' '}
        <HelpIcon>
          <MatchIcon sx={{ fontSize: '16px' }} />
        </HelpIcon>{' '}
        to find part and assembly matches. Change match type to refine results. Upload your part and assembly files from
        the <HelpLink to='assets'>Assets</HelpLink> page to get started.
      </BodyText>

      <StyledTable size='small' sx={{ marginTop: '12px' }}>
        <TableHead>
          <StyledTableHeadRow>
            <StyledTableHeadCell sx={{ whiteSpace: 'nowrap' }}>Match Type</StyledTableHeadCell>
            <StyledTableHeadCell>Description</StyledTableHeadCell>
          </StyledTableHeadRow>
        </TableHead>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>
              <BodyText color='text.primary'>Geometric</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds parts with similar geometry. Use this to find duplicate candidates, or simplification
                opportunities.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow isEven>
            <StyledTableCell>
              <BodyText color='text.primary'>Part</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds assemblies that contain parts similar to your selected part. Use this when you want to understand
                where similar parts appear across assemblies.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>
              <BodyText color='text.primary'>Visual</BodyText>
            </StyledTableCell>
            <StyledTableCell>
              <BodyText color='text.primary'>
                Finds parts that look similar. Use this when appearance is more important than part geometry.
              </BodyText>
            </StyledTableCell>
          </StyledTableRow>
          {isMetadataSearchEnabled && (
            <StyledTableRow isEven>
              <StyledTableCell>
                <BodyText color='text.primary'>Metadata</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  Finds parts with similar metadata values. Use this when part numbers, material, category, or other
                  fields are your primary matching signal.
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
          )}
          {isSuitableSubstitutesEnabled && (
            <StyledTableRow isEven={!isMetadataSearchEnabled}>
              <StyledTableCell>
                <BodyText color='text.primary'>Substitutes</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  Suggests part substitutes. Use this when you&apos;re looking for interchangeable options rather than
                  strict duplicates.
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </StyledTable>
    </>
  );
};
